import React, { useMemo, useEffect } from 'react';
import { useAppContext } from '../../../../Services/AppContext';
import {
	useInsuranceReportCreateMutation,
	useInsuranceReportUserDataQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import Text from '../../../../Components/Base/Text/Text';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import { useDiscourseContext } from '../../../Discourse/Services/DiscourseContext';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import NCZIReferenceSearchSelectField from '../../../Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import { NCZI_OID } from '../../../Nczi/Enums/ncziOid.enum';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import InsuranceOperationSelectField from '../InsuranceOperationSelect/InsuranceOperationSelectField';
import NumberField from '../../../../Components/Form/components/NumberField';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface Props {
	patientId: string;
	onCreate: () => void;
	attachToDiscourse?: boolean;
}

const InsuranceReportCreate: React.FC<Props> = ({
	patientId,
	onCreate,
	attachToDiscourse,
}) => {
	const { workplaceId } = useAppContext();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { handleAddCustomAttachmentToDiscourse } = useDiscourseContext();

	const { data: userData, isLoading } = useInsuranceReportUserDataQuery({
		id: patientId,
	});

	const validationSchema = yup.object({
		diagnosis: yup
			.mixed()
			.required(t('validation.insuranceReportCreate.diagnosis')),
		operation: yup
			.mixed()
			.required(t('validation.insuranceReportCreate.operation')),
		count: yup
			.number()
			.required(t('validation.insuranceReportCreate.count'))
			.typeError(t('validation.insuranceReportCreate.countNumber') ?? ''),
		points: yup
			.number()
			.required(t('validation.insuranceReportCreate.points'))
			.typeError(t('validation.insuranceReportCreate.pointsNumber') ?? ''),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const operationValue = formMethods.watch('operation');

	useEffect(() => {
		formMethods.setValue('count', 1);
	}, []);

	useEffect(() => {
		if (operationValue?.points) {
			formMethods.setValue('points', operationValue.points);
		}
	}, [operationValue]);

	const insuranceId: string | undefined = useMemo(() => {
		return userData?.user?.profile?.insuranceCompanyTermItemId ?? undefined;
	}, [userData]);

	const birthNumber: string | undefined = useMemo(() => {
		return userData?.user?.profile?.birthNumber ?? undefined;
	}, [userData]);

	const [createInsuranceReport] = useInsuranceReportCreateMutation();

	const handleSubmit = async (values: any) => {
		if (insuranceId && birthNumber) {
			const result = await createInsuranceReport({
				input: {
					patientId,
					insuranceId,
					birthNumber,
					workplaceId,
					diagnosis: values?.diagnosis?.label?.split(' ')?.[0],
					operationId: values?.operation?.value,
					count: parseInt(values?.count),
					points: parseInt(values?.points),
				},
			});
			if ('data' in result) {
				const insuranceReportId: string | undefined =
					result?.data?.createInsuranceReport?.id;
				if (
					attachToDiscourse &&
					handleAddCustomAttachmentToDiscourse &&
					insuranceReportId
				) {
					await handleAddCustomAttachmentToDiscourse({
						attachmentId: insuranceReportId,
						type: 'insuranceReport',
						attachmentTitle: t('base.insuranceReport') ?? '',
						sendToPatient: false,
					});
				}
				showNotificationSuccess({
					content: t('component.insuranceReportCreate.successMessage'),
				});
				onCreate();
			}
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	if (!insuranceId || !birthNumber) {
		return <Text tx='component.insuranceReportCreate.missingDataError' />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<InputWrapper>
				<NCZIReferenceSearchSelectField
					label='component.insuranceReportCreate.diagnosis'
					name='diagnosis'
					oid={NCZI_OID.DIAGNOSIS}
					showCode
					isRequired
				/>
			</InputWrapper>
			<InsuranceOperationSelectField
				label='component.insuranceReportCreate.operation'
				name='operation'
				isRequired
				insuranceId={insuranceId}
				isWrapped
			/>
			<InputWrapper>
				<NumberField
					label='component.insuranceReportCreate.count'
					name='count'
					isRequired
				/>
			</InputWrapper>
			<InputWrapper>
				<NumberField
					label='component.insuranceReportCreate.points'
					name='points'
					isRequired
				/>
			</InputWrapper>
			<SubmitButton onSubmit={handleSubmit} title='base.create' />
		</FormWrapper>
	);
};

export default InsuranceReportCreate;
